import { useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useMemo } from 'react';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { TaskPatchInput } from '../../../api/endpoints/tasks';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { useResolve } from '../../Container';
import { asyncOptionsPopupWith } from '../../popups/OptionsPopup';
import { PopupManager } from '../../popups/PopupManager';

export const toTaskDueDate = (date: Date) => {
    if (!date) return null;
    return format(date, 'yyyy-MM-dd');
};

export type TaskPatchProps = Omit<TaskPatchInput, 'id'>;

export const useOnCreateTask = (workspaceId: string, onAction?: () => void) => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();

    const mt = useMutation({
        mutationKey: ['tasks.create'],
        async mutationFn(data: TaskPatchProps) {
            onAction?.();
            const { mappings } = await api.exec(v2.tasks.create({
                workspaceId,
                items: [{
                    localId: uuid(),
                    ...data
                }]
            }));
            return Object.values(mappings)[0];
        },
        async onSettled() {
            // On complete/error, invalidate query to refetch
            await qc.invalidateQueries(['tasks.list']);
        },
    });

    return async (patch: TaskPatchProps) => mt.mutateAsync(patch);
};

export const useOnBulkPatchTasks = (taskIds: string[], onAction?: (complete: boolean) => void) => {

    const api = useResolve(ApiClient);
    const qc = useQueryClient();
    const lookup = useMemo(() => new Set(taskIds), [taskIds]);

    const mt = useMutation({
        mutationKey: ['tasks.patch'],
        async mutationFn(patch: TaskPatchProps) {
            onAction?.(true);
            const items = taskIds.map(id => ({ id, ...patch }));
            await api.exec(v2.tasks.patch({ items }));
        },
        onMutate: async (patch: TaskPatchProps) => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await qc.cancelQueries({
                predicate(query) {
                    return query.queryKey[0] === 'tasks.list';
                },
            });
            // Optimistically apply patch as applicable
            for (const q of qc.getQueryCache().getAll()) {
                if (q.queryKey[0] === 'tasks.list') {
                    const { items: prev, ...rest } = qc.getQueryData(q.queryKey) as any;
                    const next = prev.map(t => lookup.has(t.id) ? { ...t, ...patch } : t);
                    qc.setQueryData(q.queryKey, { items: next, ...rest });
                }
            }
        },
        async onSettled() {
            // On complete/error, invalidate query to refetch
            await qc.invalidateQueries(['tasks.list']);
        },
    });

    return (patch: TaskPatchProps) => mt.mutate(patch);
};

export const useOnPatchTask = (taskId: string, onAction?: (complete: boolean) => void) => {
    const taskIds = useMemo(() => [taskId], [taskId]);
    return useOnBulkPatchTasks(taskIds, onAction);
};

export const useOnBulkDeleteTasks = (taskIds: string[], onAction?: (complete: boolean) => void) => {

    const api = useResolve(ApiClient);
    const popups = useResolve(PopupManager);
    const qc = useQueryClient();
    const lookup = useMemo(() => new Set(taskIds), [taskIds]);

    const mt = useMutation({
        mutationKey: ['tasks.delete'],
        async mutationFn() {
            onAction?.(true);
            const items = taskIds.map(id => ({ id, status: 'deleted' }));
            await api.exec(v2.tasks.patch({ items }));
        },
        onMutate: async () => {
            // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
            await qc.cancelQueries({
                predicate(query) {
                    return query.queryKey[0] === 'tasks.list';
                },
            });
            // Optimistically apply delete as applicable
            for (const q of qc.getQueryCache().getAll()) {
                if (q.queryKey[0] === 'tasks.list') {
                    const { items: prev, ...rest } = qc.getQueryData(q.queryKey) as any;
                    const next = prev.filter(t => !lookup.has(t.id));
                    qc.setQueryData(q.queryKey, { items: next, ...rest });
                }
            }
        },
        async onSettled() {
            // On complete/error, invalidate query to refetch
            await qc.invalidateQueries(['tasks.list']);
        },
    });

    const confirmPopup = asyncOptionsPopupWith(
        'Delete Actions',
        'Please confirm you want to delete these actions?  ',
        [
            ['Delete Actions', 'negative', () => void (mt.mutate())]
            // [false, 'Cancel', 'common', 'cancel'],
        ]
    );

    return async () => {
        onAction?.(false);
        await popups.invoke(confirmPopup);
    };
};

export const useOnDeleteTask = (taskId: string, onAction?: () => void) => {
    const taskIds = useMemo(() => [taskId], [taskId]);
    return useOnBulkDeleteTasks(taskIds, onAction);
};
